import { useEffect, useState, type FormEvent } from 'react'
import { api, type FreundeAnsicht } from '../api'

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE')

/**
 * Freundschaften als Voraussetzung fuers Teilen. Es gibt bewusst keine Benutzerliste oder Suche: Anfragen gehen an
 * einen genau bekannten Namen oder eine Adresse, und die Oberflaeche zeigt nie, ob es den Empfaenger gibt.
 */
export function Freunde({ onGeaendert }: { onGeaendert: () => void }) {
  const [ansicht, setAnsicht] = useState<FreundeAnsicht | null>(null)
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  const laden = () =>
    api
      .freunde()
      .then(setAnsicht)
      .catch((e) => setMeldung({ text: (e as Error).message, fehler: true }))
  useEffect(() => {
    laden()
  }, [])

  const aktion = async (f: () => Promise<unknown>, erfolg: string) => {
    setMeldung(null)
    try {
      await f()
      setMeldung({ text: erfolg, fehler: false })
      await laden()
      onGeaendert()
    } catch (e) {
      setMeldung({ text: (e as Error).message, fehler: true })
    }
  }

  return (
    <div className="freunde">
      <a href="#konto" className="zurueck">
        ‹ Konto
      </a>

      {meldung && <p className={meldung.fehler ? 'hinweis fehler' : 'hinweis'}>{meldung.text}</p>}

      <Anfrage onGesendet={(an) => aktion(() => api.freundAnfragen(an), `Falls es „${an}“ gibt, ist die Anfrage angekommen.`)} />

      {!!ansicht?.eingehend.length && (
        <section className="karte">
          <h3>Anfragen an dich</h3>
          <ul className="liste">
            {ansicht.eingehend.map((a) => (
              <li key={a.benutzerId}>
                <strong>{a.name}</strong> <span className="leise klein">seit {datum(a.am)}</span>
                <div className="knoepfe links">
                  <button onClick={() => aktion(() => api.anfrageAnnehmen(a.benutzerId), `Du bist jetzt mit ${a.name} befreundet.`)}>Annehmen</button>
                  <button className="link" onClick={() => aktion(() => api.anfrageAblehnen(a.benutzerId), 'Anfrage abgelehnt.')}>
                    Ablehnen
                  </button>
                  <button
                    className="link gefahr"
                    onClick={() =>
                      confirm(`${a.name} sperren? Weitere Anfragen kommen dann nicht mehr bei dir an.`) &&
                      aktion(() => api.absenderSperren(a.benutzerId), `${a.name} gesperrt.`)
                    }
                  >
                    Sperren
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="leise klein">Wer abgelehnt oder gesperrt wird, erfährt davon nichts.</p>
        </section>
      )}

      <section className="karte">
        <h3>Freunde</h3>
        {ansicht && !ansicht.freunde.length && <p className="leise klein">Noch keine Freunde.</p>}
        <ul className="liste">
          {ansicht?.freunde.map((f) => (
            <li key={f.benutzerId}>
              <strong>{f.name}</strong> <span className="leise klein">seit {datum(f.seit)}</span>
              <div className="knoepfe links">
                <button
                  className="link"
                  onClick={() => confirm(`Freundschaft mit ${f.name} beenden?`) && aktion(() => api.freundschaftBeenden(f.benutzerId), 'Freundschaft beendet.')}
                >
                  Beenden
                </button>
                <button
                  className="link gefahr"
                  onClick={() =>
                    confirm(`Freundschaft mit ${f.name} beenden und ${f.name} sperren?`) && aktion(() => api.freundSperren(f.benutzerId), `${f.name} gesperrt.`)
                  }
                >
                  Sperren
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {!!ansicht?.ausgehend.length && (
        <section className="karte">
          <h3>Deine offenen Anfragen</h3>
          <ul className="liste">
            {ansicht.ausgehend.map((a) => (
              <li key={a.id}>
                {a.an} <span className="leise klein">seit {datum(a.am)}</span>
                <div className="knoepfe links">
                  <button className="link" onClick={() => aktion(() => api.anfrageZurueckziehen(a.id), 'Anfrage zurückgezogen.')}>
                    Zurückziehen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!ansicht?.gesperrt.length && (
        <section className="karte">
          <h3>Gesperrt</h3>
          <ul className="liste">
            {ansicht.gesperrt.map((g) => (
              <li key={g.benutzerId}>
                <strong>{g.name}</strong>
                <div className="knoepfe links">
                  <button className="link" onClick={() => aktion(() => api.entsperren(g.benutzerId), `Sperre für ${g.name} aufgehoben.`)}>
                    Sperre aufheben
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function Anfrage({ onGesendet }: { onGesendet: (an: string) => Promise<void> }) {
  const [an, setAn] = useState('')
  const [laeuft, setLaeuft] = useState(false)

  const absenden = async (e: FormEvent) => {
    e.preventDefault()
    setLaeuft(true)
    await onGesendet(an.trim())
    setAn('')
    setLaeuft(false)
  }

  return (
    <form className="karte formular" onSubmit={absenden}>
      <h3>Freund hinzufügen</h3>
      <label>
        Benutzername oder E-Mail-Adresse
        <input value={an} onChange={(e) => setAn(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={254} required />
      </label>
      <p className="leise klein">
        Es gibt kein Verzeichnis: Du brauchst den genauen Namen oder die Adresse. Ihr seid befreundet, sobald die andere Person annimmt. Befreundete
        Benutzer können später einzelne Notizen miteinander teilen.
      </p>
      <div className="knoepfe">
        <button type="submit" disabled={laeuft || !an.trim()}>
          Anfrage senden
        </button>
      </div>
    </form>
  )
}
